<template>
	<div>
		<Head :title="String(order.id)" />

		<Breadcrumbs
			href="/orders"
			:parentLabel="__('Orders')"
			:childLabel="`#${ order.id }`"
		/>

		<TrashedMessage v-if="order.deleted_at" class="mb-6" @restore="restore">
			{{ __('This order has been deleted.') }}
		</TrashedMessage>

		<div class="w-full mx-auto">
			<section aria-labelledby="order-heading" class="mt-10 border-t border-gray-200">
				<h2 id="order-heading" class="sr-only">{{ __('Order') }}</h2>

				<Icon v-if="order.deleted_at" name="trash" class="ml-2 h-3 w-3 flex-shrink-0 fill-gray-400" />

				<div class="w-full">
					<h3 class="sr-only">{{ __('Client information') }}</h3>
					<h4 class="sr-only" />

					<dl class="grid grid-cols-2 gap-x-6 py-10">
						<div>
							<dt class="text-gray-900 text-[24px] mb-[20px]">{{ __('Customer') }}</dt>

							<dd class="mt-2 text-gray-700">
								<address class="not-italic">
									<table border="0" cellpadding="4" cellspacing="0">
										<tr>
											<td class="text-[18px] mb-[8px]" style="width:120px">Namn:</td>
											<td class="text-[18px] mb-[8px]">{{ order.client.name }}</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Arbetsplats:</td>
											<td class="text-[18px] mb-[8px]">{{ order.client.workplace }}</td>
										</tr>
										<tr valign="top">
											<td class="text-[18px] mb-[8px]">Address:</td>
											<td class="text-[18px] mb-[8px]">
												{{ order.street_address }}<br/>
												{{ order.zip_code }} {{ order.city }}
											</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Avdelning:</td>
											<td class="text-[18px] mb-[8px]">{{ order.client.department ? order.client.department : '-' }}</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Roll:</td>
											<td class="text-[18px] mb-[8px]">
												{{ order.client.role ? order.client.role : '-' }}
											</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Område:</td>
											<td class="text-[18px] mb-[8px]">
												{{ order.client.speciality ? order.client.speciality : '-' }}
											</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Region:</td>
											<td class="text-[18px] mb-[8px]">{{ order.client.region ? order.client.region.name : '-' }}</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">E-post:</td>
											<td class="text-[18px] mb-[8px]">
												<a v-if="order.client.email" :href="`mailto:${order.client.email}`">
													{{ order.client.email }}
												</a>
												<span v-else>{{ order.client.email }}</span>
											</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Telefon:</td>
											<td class="text-[18px] mb-[8px]">{{ order.phone }}</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Orderpåminnelse:</td>
											<td class="text-[18px] mb-[8px]">
												<select v-model="order.client.wants_order_reminders" @change="updateReminderStatus()">
													<option value="0">Nej</option>
													<option value="1">Ja</option>
												</select>
											</td>
										</tr>
									</table>
								</address>
							</dd>
						</div>

						<div>
							<dt class="font-medium text-gray-900 text-[24px] mb-[20px]">{{ __('Information') }}</dt>

							<dd class="mt-2 text-gray-700">
								<address class="not-italic">
									<table border="0" cellpadding="4" cellspacing="0">
										<tr>
											<td class="text-[18px] mb-[8px]" style="width:140px">Ordernr:</td>
											<td class="text-[18px] mb-[8px]">{{ order.id }}</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Orderdatum:</td>
											<td class="text-[18px] mb-[8px]">{{ order.created_at }}</td>
										</tr>
										<tr>
											<td class="text-[18px] mb-[8px]">Ordermottagare:</td>
											<td class="text-[18px] mb-[8px]">{{ order.city }}</td>
										</tr>
									</table>
								</address>
							</dd>
						</div>
					</dl>
				</div>

				<h3 class="sr-only">Produkter</h3>

				<div v-for="product in order.products" :key="product.id" class="py-10 border-b border-gray-200 flex space-x-6">
					<img
						:src="product.image_url"
						:alt="product.article_name"
						class="flex-none w-20 h-20 object-center object-cover bg-gray-100 rounded-lg sm:w-20 sm:h-20"
					/>

					<div class="flex-auto flex flex-col">
						<div>
							<h4 class="text-[24px] text-gray-900">
								<a :href="`/products/${product.id}/edit`">
									{{ product.article_name }}
								</a>
							</h4>

							<p class="mt-2 text-[18px] text-gray-600">
								{{ product.info_text }}
							</p>
						</div>

						<div class="mt-6 flex-1 flex items-end">
							<dl class="flex text-sm divide-x divide-gray-700 space-x-4 sm:space-x-6">
								<div class="flex">
									<dt class="text-[16px] text-gray-900"> {{ __('Quantity') }}:</dt>
									<dd class="text-[16px] ml-2 text-gray-700">{{ product.pivot.quantity }} st</dd>
								</div>

								<div class="pl-4 flex sm:pl-6">
									<dt class="text-[16px] text-gray-900">{{ __('GCMA') }}:</dt>
									<dd class="text-[16px] ml-2 text-gray-700">{{ product.gcma ? product.gcma : '-' }}</dd>
								</div>
							</dl>
						</div>
					</div>
				</div>

				<div class="flex items-center border-t border-gray-100 bg-gray-50 px-8 py-4">
					<button
						v-if="!order.deleted_at"
						type="button"
						class="text-red-600 hover:underline"
						tabindex="-1"
						@click="destroy"
					>
						{{ __('Delete') }}
					</button>
				</div>
			</section>
		</div>
	</div>
</template>

<script>
	import { Head }       from '@inertiajs/inertia-vue3'
	import Layout 		  from '@/Shared/Layout'
	import TrashedMessage from '@/Shared/TrashedMessage'
	import Icon 		  from '@/Shared/Icon'
	import Breadcrumbs    from '@/Shared/Breadcrumbs'

	export default {
		components: {
			Head,
			Breadcrumbs,
			TrashedMessage,
			Icon
		},
		layout: Layout,
		props: {
			order: Object,
		},
		methods: {
			destroy() {
				if (confirm(this.__('Are you sure you want to delete this order?'))) {
					this.$inertia.delete(`/orders/${this.order.id}`)
				}
			},
			restore() {
				if (confirm(this.__('Are you sure you want to restore this order?'))) {
					this.$inertia.put(`/orders/${this.order.id}/restore`)
				}
			},
			updateReminderStatus() {
				this.$inertia.put(`/orders/${this.order.id}/update-reminder-status`, {
					wants_order_reminders: this.order.client.wants_order_reminders
				})
			}
		}
	}
</script>
